/**
 * Root Zustand store — the web-platform state container.
 *
 * The *state shape* comes from shared reducers (CP3); Zustand is just how the
 * web app holds + subscribes to that state. Native will pick its own
 * container (same reducers, different wiring).
 *
 * Why one root store and not eight? Zustand's selector-based subscription is
 * zero-cost when slices are independent, and one store makes the orchestrator
 * wiring simple: every orchestrator imports the single hook and can read +
 * commit across slices atomically.
 *
 * State is kept minimal here. Actions (orchestrators) live in sibling files
 * (`shopping-lists-orchestrator.ts`, etc.) that call reducers and use the
 * injected Adapters for side effects.
 */

import { create } from 'zustand';
import {
  initialAppModeState,
  initialAuthState,
  initialHouseholdState,
  initialLegacyItemsState,
  initialHouseholdProductsState,
  initialShoppingListsState,
  initialSyncStatusState,
  initialUiState,
  type AppModeState,
  type AuthState,
  type HouseholdState,
  type LegacyItemsState,
  type HouseholdProductsState,
  type ShoppingListsState,
  type SyncStatusState,
  type UiState,
} from '@shopista/shared/stores';

export interface RootState {
  auth: AuthState;
  household: HouseholdState;
  appMode: AppModeState;
  syncStatus: SyncStatusState;
  ui: UiState;
  HouseholdProducts: HouseholdProductsState;
  ShoppingLists: ShoppingListsState;
  legacyItems: LegacyItemsState;
}

export const INITIAL_ROOT_STATE: RootState = {
  auth: initialAuthState,
  household: initialHouseholdState,
  appMode: initialAppModeState,
  syncStatus: initialSyncStatusState,
  ui: initialUiState,
  HouseholdProducts: initialHouseholdProductsState,
  ShoppingLists: initialShoppingListsState,
  legacyItems: initialLegacyItemsState,
};

/**
 * Factory-style — the orchestrator integration tests create their own stores
 * rather than sharing a module-level singleton.
 */
export function createRootStore() {
  return create<RootState>(() => ({ ...INITIAL_ROOT_STATE }));
}

export type RootStore = ReturnType<typeof createRootStore>;

/** Singleton used by the running app (main.tsx). Tests use the factory. */
export const useRootStore = createRootStore();
